import { useCallback, useEffect, useState } from "react";
import { useApi, ApiError } from "./api";
import type { Channel } from "./channel";
import { withChannel } from "./channel";

export interface VisitInfo {
  chapter: string;
  lesson: string;
  ts: number;
}

export interface ProgressRecord {
  courseSlug: string;
  startedAt: number;
  lastVisited: VisitInfo | null;
  completedLessons: string[];
}

// ──────────────── URL builders ────────────────

export function progressUrl(courseSlug: string, channel: Channel = "live"): string {
  return withChannel(`/api/progress/${courseSlug}`, channel);
}

export function visitUrl(courseSlug: string, channel: Channel = "live"): string {
  return withChannel(`/api/progress/${courseSlug}/visit`, channel);
}

export function completeUrl(courseSlug: string, channel: Channel = "live"): string {
  return withChannel(`/api/progress/${courseSlug}/complete`, channel);
}

// ──────────────── Derived helpers (pure) ────────────────

export function lessonId(chapter: string, lesson: string): string {
  return `${chapter}/${lesson}`;
}

export function isCompleted(rec: ProgressRecord, chapter: string, lesson: string): boolean {
  return rec.completedLessons.includes(lessonId(chapter, lesson));
}

export function coursePercent(rec: ProgressRecord, totalLessons: number): number {
  if (totalLessons <= 0) return 0;
  return Math.round((rec.completedLessons.length / totalLessons) * 100);
}

export function resumeLocation(
  rec: ProgressRecord,
  firstIncomplete?: { chapter: string; lesson: string } | null
): { chapter: string; lesson: string } | null {
  // If the student last visited a lesson they haven't completed yet, send
  // them back there. Otherwise (last-visited is complete, or no visit yet),
  // jump to the first lesson they still owe work on.
  if (rec.lastVisited) {
    const visitedComplete = isCompleted(rec, rec.lastVisited.chapter, rec.lastVisited.lesson);
    if (!visitedComplete) {
      return { chapter: rec.lastVisited.chapter, lesson: rec.lastVisited.lesson };
    }
  }
  if (firstIncomplete) return firstIncomplete;
  // Fall back to lastVisited only if nothing else is known (e.g., course fully done).
  if (rec.lastVisited) return { chapter: rec.lastVisited.chapter, lesson: rec.lastVisited.lesson };
  return null;
}

// ──────────────── useProgress ────────────────

export type ProgressState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      progress: ProgressRecord;
      markVisited: (chapter: string, lesson: string) => Promise<void>;
      /** Resolves to the new completedLessons count (post-completion). */
      markComplete: (chapter: string, lesson: string) => Promise<number>;
      markIncomplete: (chapter: string, lesson: string) => Promise<void>;
    };

type InternalState =
  | { kind: "loading" }
  | { kind: "ready"; rec: ProgressRecord }
  | { kind: "error"; message: string };

export function useProgress(courseSlug: string, channel: Channel = "live"): ProgressState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<InternalState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!courseSlug) {
      // No course slug yet (manifest still loading). Stay in loading.
      return;
    }
    setState({ kind: "loading" });
    apiFetch<ProgressRecord>(progressUrl(courseSlug, channel))
      .then((rec) => {
        if (!cancelled) setState({ kind: "ready", rec });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, channel]);

  const markVisited = useCallback(
    async (chapter: string, lesson: string): Promise<void> => {
      if (state.kind !== "ready") return;
      const optimistic: ProgressRecord = {
        ...state.rec,
        lastVisited: { chapter, lesson, ts: Date.now() }
      };
      setState({ kind: "ready", rec: optimistic });
      try {
        const rec = await apiFetch<ProgressRecord>(visitUrl(courseSlug, channel), {
          method: "POST",
          body: JSON.stringify({ chapter, lesson })
        });
        setState({ kind: "ready", rec });
      } catch {
        // Leave the optimistic state in place; not catastrophic for visit.
      }
    },
    [courseSlug, channel, state, apiFetch]
  );

  const markComplete = useCallback(
    async (chapter: string, lesson: string): Promise<number> => {
      if (state.kind !== "ready") return 0;
      const id = lessonId(chapter, lesson);
      if (state.rec.completedLessons.includes(id)) return state.rec.completedLessons.length;
      const optimistic: ProgressRecord = {
        ...state.rec,
        completedLessons: [...state.rec.completedLessons, id]
      };
      setState({ kind: "ready", rec: optimistic });
      try {
        const rec = await apiFetch<ProgressRecord>(completeUrl(courseSlug, channel), {
          method: "POST",
          body: JSON.stringify({ chapter, lesson })
        });
        setState({ kind: "ready", rec });
        return rec.completedLessons.length;
      } catch (err) {
        // Roll back the optimistic update.
        setState({ kind: "ready", rec: state.rec });
        // Every failure propagates. Swallowing the non-412 ones and returning
        // the rolled-back count made a failed save indistinguishable from an
        // already-complete lesson: the checkmark reverted, no message was
        // shown, and the learner was left believing work was saved that never
        // was. "I finished this lesson" is the one write in the app that must
        // never fail quietly.
        throw err;
      }
    },
    [courseSlug, channel, state, apiFetch]
  );

  const markIncomplete = useCallback(
    async (chapter: string, lesson: string): Promise<void> => {
      if (state.kind !== "ready") return;
      const id = lessonId(chapter, lesson);
      if (!state.rec.completedLessons.includes(id)) return;
      const optimistic: ProgressRecord = {
        ...state.rec,
        completedLessons: state.rec.completedLessons.filter((x) => x !== id)
      };
      setState({ kind: "ready", rec: optimistic });
      try {
        const rec = await apiFetch<ProgressRecord>(completeUrl(courseSlug, channel), {
          method: "DELETE",
          body: JSON.stringify({ chapter, lesson })
        });
        setState({ kind: "ready", rec });
      } catch (err) {
        setState({ kind: "ready", rec: state.rec });
        // Same reasoning as markComplete: the rollback puts the tick back, so
        // a swallowed failure looks exactly like an undo that was ignored.
        throw err;
      }
    },
    [courseSlug, channel, state, apiFetch]
  );

  if (state.kind === "loading") return { status: "loading" };
  if (state.kind === "error") return { status: "error", message: state.message };
  return {
    status: "ready",
    progress: state.rec,
    markVisited,
    markComplete,
    markIncomplete
  };
}
